import cx from 'classnames'
import { PropsWithChildren } from 'react'
import styles from './Text.module.css'

export type TextVariant =
  // App variants
  | 'page-title'
  | 'dialog-title'
  | 'section-title'
  | 'section-title-supporting'
  | 'section-header'
  | 'invoice-number'
  | 'amount'
  | 'body'
  | 'supporting'
  | 'date'
  | 'field-label'
  | 'list'
  | 'ordered-list'
  // Content variants
  | 'eyebrow'
  | 'content-hero-eyebrow'
  | 'content-hero-supporting'
  | 'content-section-supporting'

type TextElement =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'address'
  | 'p'
  | 'span'
  | 'div'
  | 'label'
  | 'code'
  | 'ul'
  | 'ol'

const defaultElements: Record<TextVariant, TextElement> = {
  'page-title': 'h1',
  'dialog-title': 'h2',
  'section-title': 'h2',
  'section-title-supporting': 'p',
  'section-header': 'h3',
  'invoice-number': 'span',
  amount: 'span',
  body: 'p',
  supporting: 'p',
  date: 'span',
  'field-label': 'label',
  list: 'ul',
  'ordered-list': 'ol',
  eyebrow: 'span',
  'content-hero-eyebrow': 'span',
  'content-hero-supporting': 'p',
  'content-section-supporting': 'p',
}

interface TextProps {
  /**
   * The text style variant
   *
   * @default 'body'
   */
  variant?: TextVariant
  /** Override the default HTML element */
  component?: TextElement
  /** Additional CSS class */
  className?: string
}

export function Text({
  children,
  variant = 'body',
  component,
  className,
  ...props
}: PropsWithChildren<TextProps>) {
  const Component = component ?? defaultElements[variant]
  return (
    <Component
      {...props}
      className={cx(styles.Text, className, {
        [styles.isPageTitle]: variant === 'page-title',
        [styles.isDialogTitle]: variant === 'dialog-title',
        [styles.isSectionTitle]: variant === 'section-title',
        [styles.isSectionTitleSupporting]:
          variant === 'section-title-supporting',
        [styles.isSectionHeader]: variant === 'section-header',
        [styles.isInvoiceNumber]: variant === 'invoice-number',
        [styles.isAmount]: variant === 'amount',
        [styles.isBody]: variant === 'body',
        [styles.isSupporting]: variant === 'supporting',
        [styles.isDate]: variant === 'date',
        [styles.isFieldLabel]: variant === 'field-label',
        [styles.isList]: variant === 'list',
        [styles.isOrderedList]: variant === 'ordered-list',
        [styles.isEyebrow]: variant === 'eyebrow',
        [styles.isContentHeroEyebrow]:
          variant === 'content-hero-eyebrow',
        [styles.isContentHeroSupporting]:
          variant === 'content-hero-supporting',
        [styles.isContentSectionSupporting]:
          variant === 'content-section-supporting',
      })}
    >
      {children}
    </Component>
  )
}
